import EventEmitter from 'events';
import clonedeep from 'lodash.clonedeep';

export default class XProps {
  static create(store, parent, actionEngine, schema, options, props = {}) {
    return new XProps(store, parent, actionEngine, schema, options || {}, props);
  }

  /**
   * @param {Object} schema [不可变]
   * @param {Object} {mutableStates, defaultMutators} [不可变]
   * @param {Object} props {ownerScope, passThrough, iteratorId} 透传过来的数据
   */
  constructor(store, parent, actionEngine, schema, options, props) {
    this.emitter = new EventEmitter;
    this.store = store;
    this.parent = parent;
    const { __ownerScope, __passThrough, __iteratorId, __index, __sectionRendered, ...extraProps } = props; // eslint-disable-line
    this.extraProps = extraProps;
    this.ownerScope = __ownerScope || store;
    this.passThrough = __passThrough;
    this.iteratorId = __iteratorId;
    this.index = __index;
    this.sectionRendered = __sectionRendered;
    this.schema = schema;
    const propsTemplate = this.processPropsTemplate(schema, props);
    this.componentId = propsTemplate.componentId;
    this.options = options;
    this.eventsExecutor = actionEngine.createEventsExecutor(this, options.defaultMutators);
    this.usedTokens = {};
    let { index } = this;
    const needState = schema.props.createScope || schema.props.createForm || schema.props.fieldId;
    if (needState) {
      const { fieldId } = this.schema.props;
      if (typeof fieldId === 'string') {
        this.usedTokens[fieldId] = true;
      }
      const path = [];
      let _parent = this.parent;
      while (_parent) {
        // 迭代器内部，自己没有index，可能在某一级父节点上
        if (_parent && index == null && 'index' in _parent) {
          ({ index } = _parent);
        }
        // 目前只涉及section / form / repeat / 明细 /  查询 域内的变动
        if (_parent.schema.props.createForm || _parent.schema.props.createScope) {
          let parentPath = _parent.path;
          if (parentPath) {
            parentPath = parentPath.substr(0, parentPath.length - 1);
          }
          path.push(_parent.schema.props.__indexInPath__ ? index : undefined, _parent.hotProps.getValue('fieldId'), parentPath);
          break;
        }
        // keep the top parent
        if (!_parent.parent) {
          break;
        }
        _parent = _parent.parent;
      }
      // the fieldIds may be not initialed
      this.siblingFieldIds = _parent && _parent.schema.childFieldIds;
      if (path.length > 0) {
        this.path = `${path.filter(k => k !== undefined).reverse().join('/')}/`;
      }
    }
    this.hotProps = store.createHotProps(
      propsTemplate,
      this.ownerScope
    );
    // we need to init state before parse expression
    if (needState) {
      this.initState();
    }
    if (this.hotProps.hasExpression()) {
      this.hotProps.parseExpr(this, this.usedTokens, this.getDataWithPassThrough());
      if (needState) {
        // expression is changed, need sync
        this.state.setFromProps(this.hotProps);
      }
    }
    if (this.state) {
      this.state.setPath(this.path);
    }
    if (this.hotProps.hasExpression()) {
      const expression = this.hotProps.getExpression();
      const targetKeys = expression.map(exp => exp.key);
      this.willPurge = store.onStoreChange((sourceKeys) => {
        // 不涉及自己用的变量就不更新
        const needUpdate = sourceKeys.some((key) => {
          const ki = key.lastIndexOf('/');
          const actKey = ki > -1 ? key.substr(0, ki) : key;
          // simple check, if changed key in used tokens, report as need change
          return Object.keys(this.usedTokens)
            .some(k => k.substr(0, actKey.length) === actKey);
        });
        if (needUpdate && this.changeHotData(targetKeys)) {
          store.onCascadeDone(() => {
            this.emitter.emit('change');
          });
        }
      });
    }
    if (this.hotProps.hasFunction()) {
      const functions = this.hotProps.getFunction();
      functions.forEach((func) => {
        const originExpr = func.expr;
        func.expr = (...args) => originExpr.apply(this.extraProps.context.getActionContext({
          $state: this.state,
        }), args);
      });
    }
  }

  initState() {
    this.state = this.store.createState({
      props: this.hotProps,
      ownerScope: this.ownerScope,
      mutableStates: this.options.mutableStates,
      formDataFormatter: this.options.formDataFormatter,
      validators: this.options.validators,
      locale: this.extraProps.context ? this.extraProps.context.getLocale() : 'en_US',
      context: this.extraProps.context,
      componentSchema: this.schema,
      path: this.path,
    });
    // 变化时，带上targetKeys
    this.state.onChange((targetKeys) => {
      this.emitter.emit('change', targetKeys);
    });

    if (this.willPurge) {
      this.state.onPurge(this.willPurge);
    }
  }

  processPropsTemplate(schema, props) {
    let propsTemplate = { ...schema.props };
    let componentId = propsTemplate.id || propsTemplate.componentId
      || schema.id || schema.componentId;

    if (props.__iteratorId) {
      componentId = `${props.__iteratorId}-${componentId}`;
    }

    propsTemplate.componentId = componentId;
    propsTemplate.componentName = schema.componentName;
    // 兼容代码，老的 TabsLayout 组件没有 item 属性，即使升级了，schema 里仍然没有
    // 可适当时删除
    const { componentName } = schema;
    if (componentName === 'TabsLayout' && !propsTemplate.items) {
      propsTemplate.items = (schema.children || []).map(item => item.props);
    }
    // 只在明细和迭代器组件中生效，如果是 true, 则用子级的默认值覆盖父级的
    const scope = this.ownerScope;
    const { fieldId } = propsTemplate;
    if ((scope.isUseSelfDefaultValue() || scope.getValue('__mergeAfterBind')) && fieldId) {
      // 要处理含有变量的情况。
      propsTemplate = clonedeep(propsTemplate);
      const scopeValue = scope.getValue(fieldId);
      Object.keys(scopeValue || {}).forEach((key) => {
        if (key === 'dataSource' && propsTemplate[key] && typeof propsTemplate[key] === 'object') {
          // 数据源里面也会定义变量，特殊处理
          Object.keys(scopeValue[key] || {}).forEach((k) => {
            // 不是变量
            if (!this.isVariable(propsTemplate[key][k]) || this.isChanged(scopeValue[key][k])) {
              propsTemplate[key][k] = scopeValue[key][k];
            }
          });
        }
        // 不是变量，或者值已经改变时
        if (key !== 'dataSource' && (!(this.isVariable(propsTemplate[key]))
          || (this.isChanged(scopeValue)))) {
          propsTemplate[key] = scopeValue[key];
        }
      });
    }
    return propsTemplate;
  }

  isVariable(state) {
    return state && state.type === 'variable';
  }

  isChanged(state) {
    return state && state.dataType === 'CHANGED';
  }

  changeHotData() {
    // repeat未刷新，只刷新自己的时候
    const ownerScope = this.getOwnerScope();
    let passThrough = this.getDataWithPassThrough();
    if (ownerScope && ownerScope.owner) {
      // 获取scope key
      let { owner } = ownerScope;
      // Repeat / Section视为一级scope
      while (owner && (!owner.getProps || !owner.getProps().createScope)) {
        ({ owner } = owner);
      }
      if (owner) {
        const parentProps = owner.getProps();
        if (parentProps.createScope && parentProps.scope) {
          // 迭代器中的passThrought, 兼容scope
          passThrough = { [parentProps.scope]: ownerScope.owner.getData() };
        } else if (parentProps.createScope && parentProps.scopeName && parentProps.options) {
          // 兼容 scopeName
          passThrough = { [parentProps.scopeName]: parentProps.options };
        }
      }
      // passThrough = ownerScope.owner.getData();
    }
    if (!this.hotProps.update(passThrough, ownerScope, this)) {
      return false;
    }
    this.getState().setFromProps(this.hotProps);
    this.store.cascadeChange(`${this.path || ''}${this.hotProps.getValue('fieldId')}`);
    return true;
  }

  receiveProps(props) {
    const { __ownerScope, __passThrough, __iteratorId, __index, __sectionRendered, ...extraProps } = props; // eslint-disable-line
    this.passThrough = __passThrough;
    // this.iteratorId = __iteratorId;
    this.sectionRendered = __sectionRendered;
    this.index = __index;
    this.extraProps = extraProps;
    let targetKeys = [];
    if (this.hotProps.hasExpression()) {
      const expression = this.hotProps.getExpression();
      targetKeys = expression.map(exp => exp.key);
    }
    this.changeHotData(targetKeys);
    return true;
  }

  getPassThrough() {
    return this.passThrough;
  }

  isSectionRendered() {
    return this.sectionRendered || (
      this.ownerScope
      && this.ownerScope.getOwner()
      && this.ownerScope.getOwner().getProps
      && this.ownerScope.getOwner().getProps('componentName') === 'Section'
    );
  }

  getClosestScope() {
    return this.state ? this.state.getClosestScope() : this.ownerScope;
  }

  getOwnerScope() {
    return this.ownerScope;
  }

  getIteratorId() {
    return this.iteratorId;
  }

  getIndex() {
    return this.index;
  }

  getComponentId() {
    return this.componentId;
  }

  getState() {
    if (!this.state) {
      this.initState();
    }
    return this.state;
  }

  getStore() {
    return this.store;
  }

  getProps() {
    const { events, ...props } = this.hotProps.toJS();
    const _props = {
      $props: this,
      $state: this.state || null,
      ...props,
      ...this.extraProps,
      ...this.eventsExecutor.parse(events),
      ...(this.state ? this.state.toJS(true) : null),
    };
    // 绑定相关函数
    this.hotProps.getFunction().forEach(({ expr, path }) => {
      let point = _props;
      path.forEach((key, index) => {
        if (index < path.length - 1) {
          point = point[key];
        }
      });
      point[path[path.length - 1]] = expr;
    });
    return _props;
  }

  purge() {
    if (this.state) {
      this.state.purge();
    }
    if (this.willPurge) {
      this.willPurge();
    }
    this.emitter.removeAllListeners();
  }

  onChange(fn) {
    this.emitter.on('change', fn);
    return () => {
      this.emitter.removeListener('change', fn);
    };
  }

  getParentIfHave(compName) {
    let comp = this.ownerScope;
    while (comp) {
      if (comp && comp.schema && comp.schema.componentName === compName) {
        return comp;
      }
      comp = comp.owner;
    }
    return undefined;
  }

  getDataWithPassThrough() {
    const section = this.getParentIfHave('Section');
    let { passThrough } = this;
    if (section && passThrough) {
      passThrough = {
        ...passThrough,
        $data: section.getData().$data,
      };
    }
    // 明细
    const tableField = this.getParentIfHave('TableField');
    if (tableField) {
      // 要获取当前的index
      const list = this.ownerScope.owner;
      const index = (list.data && list.data.items || []).indexOf(this.ownerScope);
      passThrough = {
        scope: {
          $index: index,
          ...passThrough.scope,
        },
      };
    }
    return passThrough;
  }

  /**
   * sibling field ids, collected during Page transformComponent
   */
  getSiblingFieldIds() {
    return this.siblingFieldIds || {};
  }

  /**
   * xprops path, only care component with createForm / createScope
   */
  getPath() {
    return this.path;
  }
}
